import { MODEL_SUGGESTIONS, SUBSCRIPTION_RUNTIME_TYPES, type RuntimeProfile, type RuntimeType } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, LogIn, Sparkles } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { isCredentialError } from '@/components/chat/conversation-thread';
import { Field } from '@/components/field';
import { ModelField } from '@/components/model-field';
import { invalidateRuntimeProfiles, RuntimeLoginDialog } from '@/components/runtime-login-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage } from '@/lib/api';
import { useAgents, useRuntimeProfiles } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { profileLabel } from '@/lib/labels';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

type Provider = 'openai' | 'anthropic';
const API_PROVIDER: Record<Provider, { type: RuntimeType; name: string; model: string; label: string }> = {
  openai: { type: 'openai_api', name: 'OpenAI API', model: 'gpt-5', label: 'OpenAI / ChatGPT' },
  anthropic: { type: 'anthropic_api', name: 'Anthropic API', model: 'claude-opus-5-5', label: 'Anthropic / Claude' },
};
const SUBSCRIPTION: Record<'codex_subscription' | 'claude_subscription', { name: string; provider: string }> = {
  codex_subscription: { name: 'ChatGPT subscription', provider: 'ChatGPT' },
  claude_subscription: { name: 'Claude subscription', provider: 'Claude' },
};

/** Why an agent can't answer for real yet — or null when its model is connected. */
export function modelProblem(profile: RuntimeProfile | null | undefined, failedText?: string): 'none' | 'demo' | 'missing' | 'rejected' | null {
  if (!profile) return 'none';
  if (profile.runtimeType === 'local') return 'demo';
  if (!profile.hasCredential) return 'missing';
  if (isCredentialError(failedText)) return 'rejected';
  return null;
}

/**
 * Inline onboarding above a chat composer: connects a real model (ChatGPT/Claude login or an API key)
 * and assigns it to the agent. It is a hint, not a blocker — the demo agent still answers.
 */
export function ModelOnboarding({
  agentId,
  workspaceId,
  failedText,
}: {
  agentId: string;
  workspaceId: string;
  /** Last system error of the conversation, to re-offer the actions after a rejected key/login. */
  failedText?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const agents = useAgents(workspaceId);
  const profiles = useRuntimeProfiles(workspaceId);
  const [busy, setBusy] = useState<string | null>(null);
  const [login, setLogin] = useState<RuntimeProfile | null>(null);
  const [apiKeyOpen, setApiKeyOpen] = useState(false);
  const [switched, setSwitched] = useState<{ profile: RuntimeProfile; model: string | null } | null>(null);

  const agent = agents.data?.find((a) => a.id === agentId);
  if (!agent || !profiles.data) return null;
  const profile = profiles.data.find((p) => p.id === agent.runtimeProfileId) ?? agent.runtimeProfile ?? null;
  const problem = modelProblem(profile, failedText);

  /** Assigns the profile to the agent; `model` (if given) is the agent's own model choice — not the profile's. */
  const assign = async (p: RuntimeProfile, model?: string | null) => {
    await api.agents.update(agentId, { runtimeProfileId: p.id, ...(model !== undefined ? { model } : {}) });
    invalidateRuntimeProfiles(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['conversation'] });
    void queryClient.invalidateQueries({ queryKey: ['deskConversations'] });
    void queryClient.invalidateQueries({ queryKey: ['desk'] });
    void queryClient.invalidateQueries({ queryKey: ['agent'] });
    setSwitched({ profile: p, model: model ?? null });
  };

  const connectSubscription = async (type: 'codex_subscription' | 'claude_subscription') => {
    setBusy(type);
    try {
      // Reconnect the agent's own profile if that is the one whose login is missing/rejected.
      if (profile && profile.runtimeType === type && problem) {
        setLogin(profile);
        return;
      }
      const candidates = profiles.data.filter((p) => p.runtimeType === type && p.status === 'active');
      const connected = candidates.find((p) => p.hasCredential);
      if (connected) {
        await assign(connected);
        return;
      }
      const target =
        candidates[0] ?? (await api.runtimeProfiles.create(workspaceId, { name: SUBSCRIPTION[type].name, runtimeType: type }));
      invalidateRuntimeProfiles(queryClient);
      setLogin(target);
    } catch (err) {
      toast.error(t('That didn’t work'), errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (!problem) {
    return switched ? (
      <SuccessLine>
        {agent.effectiveModel ?? switched.model
          ? t('{agent} now uses “{profile}” with {model}. Just keep writing — your next message starts it with the new model.', {
              agent: agent.name,
              profile: switched.profile.name,
              model: (agent.effectiveModel ?? switched.model)!,
            })
          : t('{agent} now uses “{profile}”. Just keep writing — your next message starts it with the new model.', {
              agent: agent.name,
              profile: switched.profile.name,
            })}
        {SUBSCRIPTION_RUNTIME_TYPES.includes(switched.profile.runtimeType) && (
          <AgentModelChips agentId={agentId} profile={switched.profile} current={agent.model} />
        )}
      </SuccessLine>
    ) : null;
  }

  const lead =
    problem === 'demo' || problem === 'none'
      ? problem === 'demo'
        ? t('{agent} isn’t connected to an AI model yet (demo agent without AI). Connect one and it will answer properly:', { agent: agent.name })
        : t('{agent} isn’t connected to an AI model yet. Connect one and it will answer properly:', { agent: agent.name })
      : problem === 'missing'
        ? t('{agent} uses “{profile}”, but it isn’t connected yet. Connect it now:', { agent: agent.name, profile: profileLabel(profile!) })
        : t('The login for “{profile}” was rejected. Reconnect or pick a different model:', { profile: profile!.name });

  const reconnectLabel = (type: 'codex_subscription' | 'claude_subscription') =>
    profile?.runtimeType === type && (problem === 'missing' || problem === 'rejected')
      ? t('Reconnect {provider}', { provider: SUBSCRIPTION[type].provider })
      : t('Connect {provider}', { provider: SUBSCRIPTION[type].provider });

  return (
    <>
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2.5 dark:border-indigo-900 dark:bg-indigo-950/30">
        <div className="flex items-start gap-2 text-[13px] text-indigo-950 dark:text-indigo-100">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-indigo-600 dark:text-indigo-300" />
          <span>{lead}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 pl-6">
          <Button size="xs" variant="outline" className="bg-card" loading={busy === 'codex_subscription'} onClick={() => void connectSubscription('codex_subscription')}>
            {busy !== 'codex_subscription' && <LogIn />} {reconnectLabel('codex_subscription')}
          </Button>
          <Button size="xs" variant="outline" className="bg-card" loading={busy === 'claude_subscription'} onClick={() => void connectSubscription('claude_subscription')}>
            {busy !== 'claude_subscription' && <LogIn />} {reconnectLabel('claude_subscription')}
          </Button>
          <Button size="xs" variant="outline" className="bg-card" onClick={() => setApiKeyOpen(true)}>
            <KeyRound /> {t('Enter API key')}
          </Button>
        </div>
      </div>

      {login && (
        <RuntimeLoginDialog
          profile={login}
          open
          onOpenChange={(o) => !o && setLogin(null)}
          onSucceeded={() => {
            void assign(login).catch((err) => toast.error(t('Couldn’t assign the model'), errorMessage(err)));
          }}
        />
      )}
      <ApiKeyDialog
        open={apiKeyOpen}
        onOpenChange={setApiKeyOpen}
        agentName={agent.name}
        workspaceId={workspaceId}
        profiles={profiles.data}
        defaultProvider={profile?.runtimeType === 'anthropic_api' ? 'anthropic' : 'openai'}
        onSaved={assign}
      />
    </>
  );
}

function SuccessLine({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
      <span>{children}</span>
    </div>
  );
}

function ApiKeyDialog({
  open,
  onOpenChange,
  agentName,
  workspaceId,
  profiles,
  defaultProvider,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  agentName: string;
  workspaceId: string;
  profiles: RuntimeProfile[];
  defaultProvider: Provider;
  onSaved: (p: RuntimeProfile, model: string | null) => Promise<void>;
}) {
  const toast = useToast();
  const [provider, setProvider] = useState<Provider>(defaultProvider);
  const [key, setKey] = useState('');
  const [model, setModel] = useState(API_PROVIDER[defaultProvider].model);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (o: boolean) => {
    if (o) {
      setProvider(defaultProvider);
      setModel(API_PROVIDER[defaultProvider].model);
      setKey('');
    }
    onOpenChange(o);
  };

  const onProviderChange = (p: Provider) => {
    // Keep a custom model, but swap the default when it was untouched.
    if (model.trim() === '' || model === API_PROVIDER[provider].model) setModel(API_PROVIDER[p].model);
    setProvider(p);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!key.trim()) return;
    const cfg = API_PROVIDER[provider];
    setSaving(true);
    try {
      // Reuse a matching profile that has no key yet; otherwise create one.
      const reuse = profiles.find((p) => p.runtimeType === cfg.type && p.status === 'active' && !p.hasCredential);
      // The key goes onto the profile; the chosen model goes onto the agent.
      const saved = reuse
        ? await api.runtimeProfiles.update(reuse.id, { credential: key.trim() })
        : await api.runtimeProfiles.create(workspaceId, { name: cfg.name, runtimeType: cfg.type, credential: key.trim() });
      await onSaved(saved, model.trim() || null);
      setKey('');
      onOpenChange(false);
    } catch (err) {
      toast.error(t('Couldn’t save the API key'), errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={(e) => void onSubmit(e)} className="grid gap-4" autoComplete="off">
          <DialogHeader>
            <DialogTitle>{t('Enter API key')}</DialogTitle>
            <DialogDescription>
              {t('{agent} will use this model afterwards. The key is stored encrypted and never shown again.', { agent: agentName })}
            </DialogDescription>
          </DialogHeader>
          <Field label={t('Provider')}>
            <Select value={provider} onValueChange={(v) => onProviderChange(v as Provider)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(API_PROVIDER) as Provider[]).map((p) => (
                  <SelectItem key={p} value={p}>
                    {API_PROVIDER[p].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('API key')} hint={provider === 'openai' ? t('Starts with “sk-”.') : t('Starts with “sk-ant-”.')}>
            <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="new-password" spellCheck={false} required autoFocus />
          </Field>
          <ModelField
            value={model}
            onChange={setModel}
            profile={{ runtimeType: API_PROVIDER[provider].type, model: null }}
            label={t('Model for {agent}', { agent: agentName })}
            hint={t('Saved on the agent. You can change it any time in the agent settings.')}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!key.trim()}>
              {t('Save & connect')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Optional model choice for the agent right after connecting a subscription (default: leave it to the profile). */
function AgentModelChips({ agentId, profile, current }: { agentId: string; profile: RuntimeProfile; current: string | null }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [saving, setSaving] = useState<string | null>(null);
  const options = MODEL_SUGGESTIONS[profile.runtimeType] ?? [];
  if (options.length === 0) return null;
  const choose = async (model: string | null) => {
    setSaving(model ?? 'default');
    try {
      await api.agents.update(agentId, { model });
      void queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] === 'ws' && q.queryKey[2] === 'agents') || q.queryKey[0] === 'agent' });
    } catch (err) {
      toast.error(t('Couldn’t change the model'), errorMessage(err));
    } finally {
      setSaving(null);
    }
  };
  return (
    <span className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="mr-1 text-xs opacity-80">{t('Model (optional):')}</span>
      <button
        type="button"
        disabled={!!saving}
        onClick={() => void choose(null)}
        className={cn(
          'cursor-pointer rounded-md border border-emerald-300 px-1.5 py-0.5 text-[11px] dark:border-emerald-800',
          !current && 'bg-emerald-600 text-white dark:bg-emerald-500',
        )}
      >
        {profile.model ? t('Default ({model})', { model: profile.model }) : t('Default')}
      </button>
      {options.map((m) => (
        <button
          key={m}
          type="button"
          disabled={!!saving}
          onClick={() => void choose(m)}
          className={cn(
            'cursor-pointer rounded-md border border-emerald-300 px-1.5 py-0.5 font-mono text-[11px] dark:border-emerald-800',
            current === m && 'bg-emerald-600 text-white dark:bg-emerald-500',
          )}
        >
          {m}
        </button>
      ))}
    </span>
  );
}
